import { GripVertical, Send, Image as ImageIcon, Edit2, Trash2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import OverflowMenu from '@/components/shared/OverflowMenu';
import { getImageUrl } from './utils';

// ==================== SORTABLE PHOTO ITEM ====================
export function SortablePhotoItem({ photo, index, onClick, isCover, showCoverBadge, onTogglePublic, onPostcard, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group cursor-pointer overflow-hidden rounded-sm bg-white/[0.03] touch-manipulation ${isCover && showCoverBadge ? 'ring-2 ring-[#6B35B8]' : ''}`}
      {...attributes}
      {...listeners}
      data-testid={`sortable-photo-${photo.id}`}
    >
      <div onClick={(e) => { if (!isDragging) onClick(); }} className="w-full h-full">
        <img src={getImageUrl(photo.url)} alt="" loading="eager"
          className="w-full h-full object-cover transition-all duration-700 group-hover:brightness-110 group-hover:scale-105 pointer-events-none"
          draggable={false}
          onError={e => {
            console.warn('Framed: failed to load image', photo.url);
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent && !parent.querySelector('.img-error')) {
              const el = document.createElement('div');
              el.className = 'img-error';
              el.style.cssText = 'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;color:rgba(255,255,255,0.25);font-size:11px;font-family:monospace;letter-spacing:0.05em';
              el.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg><span>load failed</span>';
              parent.appendChild(el);
            }
          }} />
        {/* Photo info overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          {(photo.tagline || photo.title) && (
            <p className="text-xs text-white/90 truncate" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {photo.tagline || photo.title}
            </p>
          )}
        </div>
        {/* Cover badge */}
        {isCover && showCoverBadge && (
          <div className="absolute top-2 left-2 px-2 py-0.5 bg-[#6B35B8] rounded text-[10px] text-white uppercase tracking-wider pointer-events-none">
            Cover
          </div>
        )}
        {/* Drag indicator on hover */}
        <div className="absolute bottom-2 right-2 p-1.5 bg-black/60 rounded opacity-0 group-hover:opacity-70 transition-opacity pointer-events-none">
          <GripVertical size={14} className="text-white" />
        </div>
      </div>
      {/* Overflow menu */}
      {onTogglePublic && (
        <OverflowMenu
          publishToggle={{ isPublished: photo.is_public, onToggle: () => onTogglePublic(photo) }}
          items={[
            ...(onPostcard ? [{ icon: <Send size={16} strokeWidth={1.5} />, label: 'Make postcard', onClick: () => onPostcard(photo) }] : []),
            ...(onEdit ? [{ icon: <Edit2 size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => onEdit(photo) }] : []),
            ...(onDelete ? [{ icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => onDelete(photo), destructive: true }] : []),
          ]}
        />
      )}
    </div>
  );
}

// ==================== SORTABLE PHOTO GRID ====================
export function SortablePhotoGrid({ photos, onReorder, onPhotoClick, collection, showCoverBadge = false, onTogglePublic, onPostcard, onEdit, onDelete }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = photos.findIndex((p) => p.id === active.id);
      const newIndex = photos.findIndex((p) => p.id === over.id);
      const newOrder = arrayMove(photos, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={photos.map(p => p.id)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 auto-rows-[140px] sm:auto-rows-[180px] lg:auto-rows-[200px] gap-2 md:gap-3"
          data-testid="sortable-grid">
          {photos.map((photo, i) => (
            <SortablePhotoItem
              key={photo.id}
              photo={photo}
              index={i}
              onClick={() => onPhotoClick(photos, i)}
              isCover={collection?.cover_photo_id === photo.id}
              showCoverBadge={showCoverBadge}
              onTogglePublic={onTogglePublic}
              onPostcard={onPostcard}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
